import { useState } from 'react';
import {
  Modal,
  Box,
  Button,
  FormField,
  Input,
  SpaceBetween,
} from '@amzn/awsui-components-console';
import { UpdateGroupNameCommand } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { useAuthState } from '../authentication/context';
import { callApi } from '../client';
import { AsyncErrorBar } from '../components/AsyncErrorBar';

interface RenameGroupModalProps {
  visible: boolean;
  groupId: string;
  currentName: string;
  onDismiss: () => void;
  onSuccess: () => void;
}

const GROUP_NAME_PATTERN = /^[\x20-\x7E]{1,128}$/;

export const RenameGroupModal = ({
  visible,
  groupId,
  currentName,
  onDismiss,
  onSuccess,
}: RenameGroupModalProps) => {
  const [groupName, setGroupName] = useState(currentName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuthState();

  const resetForm = () => {
    setGroupName(currentName);
    setError(null);
  };

  const handleCancel = () => {
    resetForm();
    onDismiss();
  };

  const isValid =
    groupName.trim().length > 0 && GROUP_NAME_PATTERN.test(groupName);

  const handleSubmit = async () => {
    if (!token || !isValid) return;
    setLoading(true);
    setError(null);

    try {
      await callApi(
        new UpdateGroupNameCommand({ groupId, groupName: groupName.trim() }),
        token
      );
      onSuccess();
    } catch (err: any) {
      if (err?.statusCode === 409) {
        setError('A group with this name already exists in this venue.');
      } else {
        setError(err?.message || String(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleCancel}
      header='Rename group'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleSubmit}
              loading={loading}
              disabled={!isValid}
              data-test-id='confirm-rename-group-button'
            >
              Rename
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size='m'>
        <AsyncErrorBar error={error} header='Rename Failed' />
        <FormField
          label='New group name'
          constraintText='1–128 printable ASCII characters'
        >
          <Input
            value={groupName}
            onChange={({ detail }) => setGroupName(detail.value)}
            data-test-id='group-name-input'
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
